import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu
} from "reactstrap";
import { filterSubmenus } from "../../helpers/component_helpers";

// Componente de submenu customizado que não depende do Reactstrap
const SubMenuCustom = ({ item, originalMenus, pathname }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  
  // Buscar os subitens: primeiro tenta usar childrens do item, depois busca no originalMenus
  const subItems = useMemo(() => {
    // Se o item tem childrens, usa eles
    if (item?.childrens?.length) {
      return item.childrens;
    }
    // Caso contrário, busca no originalMenus usando o ID_Menu
    if (item?.ID_Menu && originalMenus?.length) {
      const filtered = filterSubmenus(originalMenus, item.ID_Menu);
      // Se encontrou itens no filterSubmenus, retorna eles
      if (filtered?.length) {
        return filtered;
      }
    }
    // Se não encontrou nada, retorna array vazio
    return [];
  }, [item, originalMenus]);
  
  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      const updatePosition = () => {
        if (wrapperRef.current) {
          // Busca o dropdown-item pai que contém o wrapper
          const dropdownItem = wrapperRef.current.closest('.dropdown-item');
          if (dropdownItem) {
            const itemRect = dropdownItem.getBoundingClientRect();
            // Posiciona o submenu colado no dropdown-item (sem espaçamento)
            setPosition({
              top: itemRect.top,
              left: itemRect.right // Sem espaçamento adicional
            });
          } else {
            // Fallback: usa o wrapper se não encontrar o dropdown-item
            const rect = wrapperRef.current.getBoundingClientRect();
            setPosition({
              top: rect.top,
              left: rect.right // Sem espaçamento adicional
            });
          }
        }
      };
      
      // Atualiza imediatamente
      updatePosition();
      
      // Adiciona listeners para atualizar quando necessário
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);
  
  const submenuContent = isOpen ? (
    <div 
      className="submenu-custom"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: 'auto',
        minWidth: '200px'
      }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={(e) => {
        // Adiciona delay para dar tempo do usuário clicar no link
        setTimeout(() => {
          const relatedTarget = e.relatedTarget;
          // Verifica se relatedTarget é um elemento DOM válido antes de usar closest
          if (relatedTarget && relatedTarget.nodeType === Node.ELEMENT_NODE) {
            // Se o mouse foi para o wrapper ou ainda está sobre o submenu, não fecha
            if (!relatedTarget.closest('.submenu-custom-wrapper') && 
                !relatedTarget.closest('.submenu-custom')) {
              setIsOpen(false);
            }
          } else {
            // Verifica se ainda está sobre o submenu antes de fechar
            const submenu = document.querySelector('.submenu-custom');
            if (!submenu || !submenu.matches(':hover')) {
              setIsOpen(false);
            }
          }
        }, 200); // Delay maior para dar tempo de clicar
      }}
      onClick={(e) => {
        // Se o clique foi em um link, permite a navegação
        if (e.target.closest('a')) {
          return; // Deixa o Link do React Router funcionar
        }
        // Caso contrário, previne propagação para não fechar outros menus
        e.stopPropagation();
      }}
    >
      {subItems?.map((subItem, subKey) => {
        // Garante que URL_Transacao esteja preenchido (pode vir como Codigo_Transacao)
        // Se não tiver URL_Transacao nem Codigo_Transacao, não renderiza o link (item fantasma)
        const urlTransacao = subItem?.URL_Transacao || subItem?.Codigo_Transacao;
        const temTransacao = urlTransacao && urlTransacao.trim() !== "";
        
        // Se não tem transação, não renderiza como link clicável
        if (!temTransacao) {
          return (
            <div 
              key={subKey} 
              className="dropdown-item"
              style={{ padding: '8px 12px', cursor: 'default', opacity: 0.6 }}
            >
              <div className="d-flex align-items-center gap-2" style={{ width: '100%' }}>
                {subItem.Icone_Transacao && (
                  <i className={subItem.Icone_Transacao} />
                )}
                <span className="font-size-14 font-w-400" style={{ whiteSpace: 'nowrap' }}>{subItem?.Transacao || subItem?.Descricao}</span>
              </div>
            </div>
          );
        }
        
        return (
          <div 
            key={subKey} 
            className="dropdown-item"
            style={{ padding: '8px 12px' }}
            onMouseEnter={() => setIsOpen(true)} // Mantém aberto quando mouse está sobre o item
          >
            <Link 
              to={"/" + urlTransacao}
              className="d-flex align-items-center gap-2"
              style={{ textDecoration: 'none', width: '100%' }}
              onMouseDown={(e) => {
                // Mantém o submenu aberto durante o clique
                e.stopPropagation();
              }}
            >
              {subItem.Icone_Transacao && (
                <i className={subItem.Icone_Transacao} />
              )}
              <span className="font-size-14 font-w-400" style={{ whiteSpace: 'nowrap' }}>{subItem?.Transacao || subItem?.Descricao}</span>
            </Link>
          </div>
        );
      })}
    </div>
  ) : null;
  
  return (
    <>
      <div 
        ref={wrapperRef}
        className="submenu-custom-wrapper"
        style={{ position: 'relative', width: '100%' }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={(e) => {
          // Só fecha se o mouse realmente saiu (não está indo para o submenu)
          setTimeout(() => {
            if (wrapperRef.current && !wrapperRef.current.matches(':hover')) {
              const submenu = document.querySelector('.submenu-custom');
              if (!submenu || !submenu.matches(':hover')) {
                setIsOpen(false);
              }
            }
          }, 100);
        }}
        onClick={(e) => {
          // Não bloqueia cliques em links
          if (!e.target.closest('a')) {
            e.stopPropagation();
          }
        }}
      >
        <div 
          className="submenu-toggle d-flex justify-content-between align-items-center w-100"
          style={{ 
            padding: '8px 12px', 
            cursor: 'pointer',
            color: 'inherit'
          }}
        >
          <div className="d-flex gap-2 align-items-center">
            {item?.Icone_Transacao && (
              <i className={item?.Icone_Transacao} />
            )}
            <span className="font-size-14 font-w-400" style={{ whiteSpace: 'nowrap' }}>{item?.Transacao || "Dropdown"}</span>
          </div>
          <i className={`font-size-16 fas fa-angle-${isOpen ? 'left' : 'right'}`}></i>
        </div>
      </div>
      {ReactDOM.createPortal(submenuContent, document.body)}
    </>
  );
};

// Componente para renderizar dropdown principal via Portal (para menus controlados)
const MainMenuPortal = ({ items, originalMenus, pathname, isOpen, toggleRef, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && toggleRef?.current) {
      const updatePosition = () => {
        if (toggleRef.current) {
          // Busca o botão dentro do wrapper
          const button = toggleRef.current.querySelector('button.dropdown-custom') || toggleRef.current;
          const rect = button.getBoundingClientRect();
          setPosition({
            top: rect.bottom,
            left: rect.left
          });
        }
      };
      
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      // Handler para fechar ao clicar fora
      const handleClickOutside = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target) && 
            toggleRef.current && !toggleRef.current.contains(e.target)) {
          if (onClose) {
            onClose();
          }
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, toggleRef, onClose]);
  
  if (!isOpen) return null;
  
  const menuContent = (
    <div 
      ref={menuRef}
      className="dropdown-menu show main-menu-portal"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1003,
        width: 'auto',
        minWidth: '200px'
      }}
      onClick={(e) => {
        const target = e.target.closest('.submenu-custom-wrapper');
        if (target) {
          e.stopPropagation();
        }
      }}
    >
      {items?.map((item, key) => (
        <div 
          key={key} 
          className={`dropdown-item ${("/"+(item?.URL_Transacao || item?.Codigo_Transacao || "")) === pathname ? 'actived' : ''}`} 
          style={{ position: 'relative', overflow: 'visible', padding: '8px 12px' }}
          onClick={(e) => {
            if (item?.Dropdown === 'S') {
              e.stopPropagation();
            }
          }}
        >              
          { item?.Dropdown === 'S' ?
            <SubMenuCustom 
              item={item}
              originalMenus={originalMenus}
              pathname={pathname}
            />
          :                 
            <Link 
              key={key} 
              to={(item?.URL_Transacao || item?.Codigo_Transacao) ? "/" + (item?.URL_Transacao || item?.Codigo_Transacao) : "#"} 
              className="font-size-14 font-w-400 d-flex align-items-center gap-2" 
              style={{ textDecoration: 'none', width: '100%' }}
            >  
              {item.Icone_Transacao && (
                <i className={item.Icone_Transacao} />
              )}
              <span className="d-none d-xl-inline-block" style={{ whiteSpace: 'nowrap' }}>{item?.Transacao || item?.Descricao}</span>
            </Link>
          }
          {" "}
        </div>
      ))}
    </div>
  );
  
  return ReactDOM.createPortal(menuContent, document.body);
};

const DropdownCustom = ({items, originalDataMenus, direction, icon, label, isSubDropdown, menuId, isOpen, onToggle}) => {
  
  const [menu, setMenu] = useState(false);
  const [originalMenus, ] = useState(originalDataMenus)
  const {pathname} = useLocation()
  const toggleRef = useRef(null);
  
  // Gera um ID único para cada dropdown baseado no label e menuId
  const uniqueId = useMemo(() => {
    const baseId = menuId ? `menu-${menuId}` : label?.replace(/\s+/g, '-').toLowerCase() || Math.random().toString(36).substr(2, 9);
    return `dropdown-custom-${baseId}`
  }, [label, menuId])

  // Se menuId, isOpen e onToggle forem fornecidos, usa controle externo (para menus principais)
  // Caso contrário, usa controle interno (para submenus)
  const isControlled = menuId !== undefined && isOpen !== undefined && onToggle !== undefined;
  const dropdownIsOpen = isControlled ? isOpen : menu;
  
  // Toggle customizado
  const toggleDropdown = isControlled ? onToggle : () => setMenu(!menu);
  
  // Handler para abrir submenu ao passar o mouse sobre um item que tem submenu
  const handleMouseEnter = (e, hasDropdown) => {
    if (hasDropdown) {
      e.stopPropagation();
      // Se não é controlado (submenu interno), abre ao passar o mouse
      if (!isControlled && !menu) {
        setMenu(true);
      }
    }
  };
  
  // Handler para manter submenu aberto ao passar o mouse
  const handleMouseLeave = (e, hasDropdown) => {
    if (hasDropdown) {
      e.stopPropagation();
    }
  };

  return (
    <div className="dropdown-wrapper" ref={isControlled ? toggleRef : null} style={{ position: 'relative', display: 'inline-block' }}>
      <Dropdown
        isOpen={isControlled ? false : dropdownIsOpen} // Não usa Reactstrap para menus controlados
        toggle={toggleDropdown}
        direction={direction ? direction : "down"}
        className="inline-block"
        id={uniqueId}
      >
        <DropdownToggle
          className={`btn dropdown-custom inline-block ${isControlled && dropdownIsOpen ? 'is-open' : ''}`}
          id={uniqueId}
          tag="button"
          aria-expanded={isControlled ? dropdownIsOpen : undefined}
          onClick={(e) => {
            // Previne que o clique no submenu feche o dropdown pai
            if (isSubDropdown) {
              e.stopPropagation();
              // Garante que o toggle funcione mesmo com stopPropagation
              if (!isControlled) {
                setMenu(!menu);
              }
            }
          }}
        >
          <div className="d-flex justify-content-between align-items-center gap-2 w-100 font-size-14">
            <div className="d-flex gap-2 align-items-center">
              {icon && (
                <i className={`${icon}`} />
              )}
              <span className="d-none d-xl-inline-block" style={{ whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            <i className={`font-size-16 fas fa-angle-${ dropdownIsOpen ? isSubDropdown ? 'left' : 'up' :  isSubDropdown ? 'right' : 'down'}`}></i>
          </div>
        </DropdownToggle>
        {!isControlled && (
          <DropdownMenu 
            data-popper-placement={isSubDropdown ? "right-start" : "bottom-start"} 
            style={{ position: 'absolute', top: isSubDropdown ? '0' : '100%', left: isSubDropdown ? '100%' : '0', right: 'auto' }}
            onClick={(e) => {
              // Previne que cliques em itens com submenu fechem o dropdown
              const target = e.target.closest('.submenu-custom-wrapper');
              if (target) {
                e.stopPropagation();
              }
            }}
            modifiers={[
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                },
              },
            ]}
          >
            {items?.map((item, key) => (
              <div 
                key={key} 
                className={`dropdown-item ${("/"+(item?.URL_Transacao || item?.Codigo_Transacao || "")) === pathname ? 'actived' : ''}`} 
                style={{ position: 'relative', overflow: 'visible' }}
                onMouseEnter={(e) => handleMouseEnter(e, item?.Dropdown === 'S')}
                onMouseLeave={(e) => handleMouseLeave(e, item?.Dropdown === 'S')}
                onClick={(e) => {
                  // Se tem submenu, previne que feche o dropdown pai mas permite o toggle funcionar
                  if (item?.Dropdown === 'S') {
                    e.stopPropagation();
                  }
                }}
              >              
                { item?.Dropdown === 'S' ?
                  <SubMenuCustom 
                    item={item}
                    originalMenus={originalMenus}
                    pathname={pathname}
                  />
                :                 
                  <Link 
                    key={key} 
                    to={(item?.URL_Transacao || item?.Codigo_Transacao) ? "/" + (item?.URL_Transacao || item?.Codigo_Transacao) : "#"} 
                    className="font-size-14 font-w-400 d-flex align-items-center gap-2" 
                    style={{ textDecoration: 'none', width: '100%' }}
                  >  
                    {item.Icone_Transacao && (
                      <i className={item.Icone_Transacao} />
                    )}
                    <span className="d-none d-xl-inline-block" style={{ whiteSpace: 'nowrap' }}>{item?.Transacao || item?.Descricao}</span>
                  </Link>
                }
                {" "}
              </div>
            ))}
          </DropdownMenu>
        )}
      </Dropdown>
      {isControlled && (
        <MainMenuPortal 
          items={items}
          originalMenus={originalMenus}
          pathname={pathname}
          isOpen={dropdownIsOpen}
          toggleRef={toggleRef}
          onClose={onToggle}
        />
      )}
    </div>
  );
};

export default DropdownCustom
